import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const styles = {
  container: {
    position: 'fixed',
    top: '16px',
    right: '16px',
    zIndex: 9999,
    maxWidth: '360px',
    transition: 'transform 0.3s ease-out, opacity 0.3s ease-out',
  },
  toast: {
    background: 'linear-gradient(135deg, rgba(120, 53, 15, 0.95) 0%, rgba(146, 64, 14, 0.95) 100%)',
    backdropFilter: 'blur(12px)',
    borderRadius: '12px',
    border: '1px solid rgba(251, 191, 36, 0.5)',
    boxShadow: '0 25px 50px -12px rgba(251, 191, 36, 0.25)',
    overflow: 'hidden',
  },
  progressBar: {
    height: '3px',
    background: 'rgba(251, 191, 36, 0.3)',
  },
  progressFill: {
    height: '100%',
    background: '#fbbf24',
    transition: 'width linear',
  },
  content: {
    padding: '16px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
  },
  iconWrapper: {
    flexShrink: 0,
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    background: 'rgba(251, 191, 36, 0.2)',
    border: '2px solid #fbbf24',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '28px',
    animation: 'bounce 1s infinite',
  },
  textContent: {
    flex: 1,
    minWidth: 0,
  },
  label: {
    color: '#fbbf24',
    fontSize: '11px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '4px',
  },
  title: {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: '18px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginBottom: '4px',
  },
  description: {
    color: 'rgba(254, 243, 199, 0.8)',
    fontSize: '14px',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  points: {
    marginTop: '8px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    background: 'rgba(251, 191, 36, 0.2)',
    borderRadius: '9999px',
  },
  pointsValue: {
    color: '#fbbf24',
    fontSize: '14px',
    fontWeight: 'bold',
  },
  pointsLabel: {
    color: 'rgba(251, 191, 36, 0.8)',
    fontSize: '12px',
  },
  closeButton: {
    flexShrink: 0,
    padding: '4px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    borderRadius: '50%',
    transition: 'background 0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeIcon: {
    width: '20px',
    height: '20px',
    color: 'rgba(251, 191, 36, 0.7)',
  },
  keyframes: `
    @keyframes bounce {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-5px); }
    }
    @keyframes shrink {
      from { width: 100%; }
      to { width: 0%; }
    }
  `,
};

function AchievementToast({ achievement, onClose, duration = 5000 }) {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    // Animate in
    const showTimer = setTimeout(() => setIsVisible(true), 50);

    // Auto close
    const closeTimer = setTimeout(() => {
      handleClose();
    }, duration);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(closeTimer);
    };
  }, [duration]);

  const handleClose = () => {
    setIsLeaving(true);
    setTimeout(onClose, 300);
  };

  const transform = isVisible && !isLeaving ? 'translateX(0)' : 'translateX(100%)';
  const opacity = isVisible && !isLeaving ? 1 : 0;

  return (
    <>
      <style>{styles.keyframes}</style>
      <div style={{ ...styles.container, transform, opacity }}>
        <div style={styles.toast}>
          {/* Progress bar */}
          <div style={styles.progressBar}>
            <div
              style={{
                ...styles.progressFill,
                animation: `shrink ${duration}ms linear forwards`,
              }}
            />
          </div>

          <div style={styles.content}>
            {/* Icon */}
            <div style={styles.iconWrapper}>
              {achievement.icon || '🏆'}
            </div>

            {/* Content */}
            <div style={styles.textContent}>
              <div style={styles.label}>{t('achievement.unlocked')}</div>
              <h4 style={styles.title}>{achievement.name}</h4>
              <p style={styles.description}>{achievement.description}</p>
              {achievement.reward_points > 0 && (
                <div style={styles.points}>
                  <span style={styles.pointsValue}>+{achievement.reward_points}</span>
                  <span style={styles.pointsLabel}>{t('achievement.points')}</span>
                </div>
              )}
            </div>

            {/* Close button */}
            <button
              onClick={handleClose}
              style={styles.closeButton}
              onMouseEnter={e => e.target.style.background = 'rgba(255, 255, 255, 0.1)'}
              onMouseLeave={e => e.target.style.background = 'transparent'}
            >
              <svg style={styles.closeIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// Container for multiple toasts
export function AchievementToastContainer({ achievements, onClear }) {
  const [queue, setQueue] = useState([]);
  const [current, setCurrent] = useState(null);

  useEffect(() => {
    if (achievements && achievements.length > 0) {
      setQueue(prev => [...prev, ...achievements]);
      onClear();
    }
  }, [achievements, onClear]);

  useEffect(() => {
    if (!current && queue.length > 0) {
      setCurrent(queue[0]);
      setQueue(prev => prev.slice(1));
    }
  }, [current, queue]);

  const handleClose = () => {
    setCurrent(null);
  };

  if (!current) return null;

  return <AchievementToast achievement={current} onClose={handleClose} />;
}

export default AchievementToast;
